// Winnings tax calculator block (calculator/winnings-tax), markup in inc/core/gutenberg/blocks/block-winnings-tax-calculator.php.
// Skatteloven § 5-50: a single win above LIMIT from a commercial game is taxed in full, minus the stake in that game.

const LIMIT = 10000;
const RATE = 0.22;
const RATE_NORTH = 0.185; // Finnmark and Nord-Troms.
const TAX_FREE_REPORT_LIMIT = 100000; // Tax-free wins above this per year still go into the tax return.

const numberFormat = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });
const kr = (n) => numberFormat.format(Math.round(n)) + ' kr';
const percent = (rate) => String(rate * 100).replace('.', ',') + ' %';
// Reads "25 000", "25.000", "kr 25.000,-", "1,000,000" and "25 000,50": kroner have at most two decimals,
// so a dot or comma before exactly three digits groups thousands and any other one is the decimal separator.
const parse = (value) => {
  const n = parseFloat(String(value || '').trim()
    .replace(/\s|kr|,-$/gi, '')
    .replace(/[.,](?=\d{3}(?!\d))/g, '')
    .replace(',', '.'));
  return isNaN(n) ? NaN : n;
};

// Big amount: the number with a smaller "kr".
const setAmount = (element, n) => {
  const unit = document.createElement('small');
  unit.textContent = 'kr';
  element.replaceChildren(numberFormat.format(Math.round(n)) + ' ', unit);
};

// Note under the table: [label, text] or null.
const getNote = (taxable, taxFreeSource, win, left) => {
  if (taxable && left < LIMIT) {
    return ['Merk:', 'Gevinster rett over 10 000 kr kan gi deg mindre igjen enn en skattefri gevinst på nøyaktig 10 000 kr, fordi hele gevinsten blir skattepliktig når grensen passeres.'];
  }
  if (taxFreeSource && win > TAX_FREE_REPORT_LIMIT) {
    return ['Husk:', 'Selv om gevinsten er skattefri, må du oppgi den i skattemeldingen når du har vunnet mer enn 100 000 kr til sammen i skattefrie spill og lotterier i løpet av året.'];
  }
  if (taxable) {
    return ['Tips:', 'Ta vare på spillhistorikk og transaksjoner. Du trenger ikke sende dokumentasjon med skattemeldingen, men må kunne legge den fram hvis Skatteetaten ber om det. Tap i andre spill kan ikke trekkes fra.'];
  }
  return null;
};

const getStatusText = (taxable, source, win, rate) => {
  if (taxable) {
    return 'Gevinsten på ' + kr(win) + ' overstiger grensen på 10 000 kr og kommer fra et kommersielt spill. Hele gevinsten, minus innsatsen, beskattes med ' + percent(rate) + ' og må føres i skattemeldingen.';
  }
  if (source === 'norsk') {
    return 'Gevinster fra Norsk Tipping og Norsk Rikstoto er skattefrie uansett størrelse.';
  }
  if (source === 'eos-ideell') {
    return 'Gevinster fra EØS-spill som tilsvarer lovlige norske spill, er under offentlig tilsyn og ikke har kommersielt formål, er skattefrie uansett størrelse. Du må kunne dokumentere at vilkårene er oppfylt.';
  }
  return 'Enkeltgevinster på 10 000 kr eller mindre er skattefrie, også fra kommersielle nettcasinoer.';
};

const initCalculator = (root) => {
  const form = root.querySelector('.winnings-tax-calculator__form');
  const field = (name) => root.querySelector(`[data-field="${name}"]`);
  const winInput = field('win');
  const stakeInput = field('stake');
  const sourceInput = field('source');
  const northInput = field('north');
  const error = root.querySelector('.winnings-tax-calculator__error');
  const result = root.querySelector('.winnings-tax-calculator__result');
  const status = root.querySelector('.winnings-tax-calculator__status');
  const note = root.querySelector('.winnings-tax-calculator__note');
  const output = (name) => root.querySelector(`[data-output="${name}"]`);

  let isLive = false; // After the first result, every change recalculates.

  // showErrors: the button shows the message; while typing, an unfinished amount only hides the result.
  const calculate = (showErrors) => {
    const win = parse(winInput.value);
    const stake = stakeInput.value.trim() === '' ? 0 : parse(stakeInput.value);
    const source = sourceInput.value;

    let message = null;
    if (isNaN(win) || win <= 0) {
      message = 'Skriv inn en gyldig gevinst (større enn 0 kr).';
    } else if (isNaN(stake) || stake < 0) {
      message = 'Skriv inn en gyldig innsats (0 kr eller mer).';
    }
    if (message) {
      error.textContent = message;
      if (showErrors) {
        error.hidden = false;
      }
      result.hidden = true;
      return;
    }
    error.hidden = true;

    const rate = northInput.checked ? RATE_NORTH : RATE;
    const taxFreeSource = source !== 'kommersiell';
    const taxable = !taxFreeSource && win > LIMIT;
    const base = taxable ? Math.max(0, win - stake) : 0;
    const tax = base * rate;
    const left = win - tax;
    const net = win - stake - tax;

    status.classList.toggle('winnings-tax-calculator__status--tax', taxable);
    status.classList.toggle('winnings-tax-calculator__status--free', !taxable);
    status.querySelector('.winnings-tax-calculator__status-icon').textContent = taxable ? '!' : '✓';
    status.querySelector('.winnings-tax-calculator__status-title').textContent = taxable ? 'Gevinsten er skattepliktig' : 'Gevinsten er skattefri';
    status.querySelector('.winnings-tax-calculator__status-text').textContent = getStatusText(taxable, source, win, rate);

    setAmount(output('tax'), tax);
    setAmount(output('net'), net);
    output('row-win').textContent = kr(win);
    output('row-stake').textContent = taxable ? '− ' + kr(Math.min(stake, win)) : 'Ikke relevant';
    output('row-base').textContent = kr(base);
    output('row-rate').textContent = taxable ? percent(rate) : '0 %';
    output('row-tax').textContent = kr(tax);
    output('row-left').textContent = kr(left);

    const noteText = getNote(taxable, taxFreeSource, win, left);
    note.replaceChildren();
    if (noteText) {
      const label = document.createElement('strong');
      label.textContent = noteText[0];
      note.append(label, ' ' + noteText[1]);
    }
    note.hidden = !noteText;

    result.hidden = false;
    isLive = true;
  };

  // Typed amounts get thousand separators when the field is left.
  [winInput, stakeInput].forEach((input) => {
    input.addEventListener('blur', () => {
      const n = parse(input.value);
      if (input.value.trim() !== '' && !isNaN(n)) {
        input.value = numberFormat.format(Math.round(n));
      }
    });
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    calculate(true);
  });

  // Once a result has been shown, it follows every change of the form.
  const recalculate = () => {
    if (isLive) {
      calculate(false);
    }
  };
  [winInput, stakeInput].forEach((input) => input.addEventListener('input', recalculate));
  [sourceInput, northInput].forEach((input) => input.addEventListener('change', recalculate));
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-winnings-tax-calculator]').forEach(initCalculator);
});
